// Strips prefixes like IRMP, IRMCPL, IRMP-O-, IRMPCPL-O- etc. and keeps only
// the trailing 12-digit number so the barcode encodes a clean numeric value —
// same rule originally used by printPatientCard.js, now shared by every
// print document that carries a patient/couple/spouse code barcode.
export function barcodeValue(id) {
  return (id || '').replace(/\D/g, '').slice(-12);
}

// For a document's own number (bill_no, credit_note_no, procedure code,
// etc.) — these are usually PREFIX-PERIOD-SEQUENCE style (e.g.
// "OPDB-202607-00042", "IRMP-O-2026-00318"): strip everything up to and
// including the last dash, keeping just the trailing sequence segment. If
// there's no dash, the code is used as-is.
export function documentCodeValue(code) {
  if (!code) return '';
  const str = String(code);
  const idx = str.lastIndexOf('-');
  return idx >= 0 ? str.slice(idx + 1) : str;
}

export const BARCODE_CDN_SCRIPT =
  '<script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>';

/**
 * Builds a compact barcode block for window.open() + document.write()
 * print templates (pharmacy/OPD prints) — stacked vertically by default
 * (fits a narrow side column) or side-by-side with `direction: 'row'`
 * (fits a full-width strip under a patient/doctor table).
 *
 * `items`: array of { label, code, transform? } — `transform` defaults to
 * barcodeValue (patient/couple/spouse's 12-digit-tail rule); pass
 * documentCodeValue for a document's own bill/invoice/note number. Items
 * with no `code` are skipped (e.g. a walk-in bill with no couple).
 *
 * Returns { html, initScript }:
 *   - insert `html` wherever the block should appear in the document body
 *   - append `initScript` via barcodeScriptBlock(), placed immediately
 *     after `html` in the same document.write() stream — document.write'd
 *     <script> tags execute synchronously in source order, so by the time
 *     the init script runs, both the CDN script (JsBarcode global) and the
 *     <svg> placeholders from `html` already exist. Do NOT wrap this in
 *     window.onload — on multi-page documents that call this per page,
 *     window.onload=fn assignments overwrite each other and only the last
 *     page's barcodes would ever render.
 */
export function codeBarcodeStack({ idPrefix, items, direction = 'column' }) {
  const resolved = (items || [])
    .filter(it => it && it.code)
    .map((it, i) => ({
      key: `i${i}`,
      label: it.label,
      value: (it.transform || barcodeValue)(it.code),
      pushRight: !!it.pushRight,
    }));

  if (!resolved.length) return { html: '', initScript: '' };

  const isRow = direction === 'row';
  const html = `
    <div class="pt-barcode-stack" style="display:flex;flex-direction:${direction};gap:${isRow ? '6mm' : '0.8mm'};align-items:${isRow ? 'flex-end' : 'flex-start'};flex-wrap:wrap;">
      ${resolved.map(it => `
        <div style="${isRow ? 'text-align:center;' : ''}${isRow && it.pushRight ? 'margin-left:auto;' : ''}">
          <div style="font-size:5pt;color:#00798E;font-weight:700;text-transform:uppercase;line-height:1.2;">${it.label}</div>
          <svg id="${idPrefix}-${it.key}"></svg>
        </div>`).join('')}
    </div>`;

  const initScript = resolved
    .map(it => `JsBarcode('#${idPrefix}-${it.key}','${it.value}',{format:'CODE128',width:1,height:14,displayValue:true,fontSize:5.5,margin:1});`)
    .join('\n');

  return { html, initScript };
}

/** Back-compat shorthand for the common Patient/Couple/Spouse trio (see codeBarcodeStack). */
export function patientBarcodeStrip({ idPrefix, patientCode, coupleCode, spouseCode, documentCode, documentLabel, direction }) {
  return codeBarcodeStack({
    idPrefix,
    direction,
    items: [
      { label: 'Patient ID', code: patientCode },
      { label: 'Couple ID', code: coupleCode },
      { label: 'Spouse ID', code: spouseCode },
      documentCode ? { label: documentLabel || 'Doc No', code: documentCode, transform: documentCodeValue, pushRight: true } : null,
    ].filter(Boolean),
  });
}

/**
 * Contact objects across this codebase carry couple/spouse info under
 * different shapes depending on which endpoint populated them — sometimes
 * flattened onto the contact (`couple_code`, `spouse.external_code`),
 * sometimes nested Couple-model associations (`patientCouple`/`spouseCouple`,
 * each carrying its own `external_code` and a `spouse`/`patient` sub-contact).
 * This tries both without needing to know which shape a given call site has.
 */
export function resolveCoupleSpouseCodes(contact) {
  if (!contact) return { coupleCode: undefined, spouseCode: undefined };
  const coupleCode =
    contact.couple_code ||
    contact.patientCouple?.external_code ||
    contact.spouseCouple?.external_code ||
    undefined;
  const spouseCode =
    contact.spouse?.external_code ||
    contact.patientCouple?.spouse?.external_code ||
    contact.spouseCouple?.patient?.external_code ||
    undefined;
  return { coupleCode, spouseCode };
}

/**
 * Wraps one or more initScripts into a plain <script>...</script> block
 * alongside the CDN script tag — no window.onload wrapper (see
 * codeBarcodeStack's doc comment for why that's unsafe on multi-call/
 * multi-page documents).
 */
export function barcodeScriptBlock(...initScripts) {
  const combined = initScripts.filter(Boolean).join('\n');
  if (!combined) return '';
  return [BARCODE_CDN_SCRIPT, `<script>${combined}</script>`].join('\n');
}
